import { useCallback, useMemo, useState } from 'react'
import { getProfile } from '../../api/auth'
import { getCart, getCouponsMaximum, postGuestCart } from '../../api/cart'
import { getOrderList } from '../../api/orderHistory'
import { getCategories } from '../../api/product'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { findObjectsWithValue, getParsedLocalStorageItem, getParsedSessionStorageItem } from '../../js/script'
import { SET_CATEGORIES, SET_USER_CREDIT, SET_USER_PROFILE, useCartGroupContext } from './CartGroupProvider'

export default function useCartGroup() {
  const { hasAccessToken, setIsLottie } = useCommonContext()

  const { state, dispatch } = useCartGroupContext()
  const {
    isInvalid,
    userProfile,
    userCredit,
    orderProductMap,
    orderVariationMap,
    invalidOrderVariationMap,
    selectOrderVariationMap,
    totalCartPriceInfo,
    categories,
  } = state

  const [firstPurchase, setFirstPurchase] = useState<boolean>(false) // 첫구매 확인인데 false가 첫구매, true가 첫구매아님

  const selectVariationCartNos = useMemo(() => {
    return Object.values(selectOrderVariationMap).map((varition) => varition.cartNo)
  }, [selectOrderVariationMap])

  const createGTMItems = useCallback(
    async (variations: any[]) => {
      try {
        if (categories.flatCategories?.length === 0) return

        const selectedCategory: any[] = []
        variations.forEach((variation) => {
          variation.categoryNos.forEach((categoryNo: number) => {
            const [findCategory] = findObjectsWithValue(categories.flatCategories, categoryNo)

            if (findCategory && findCategory.depth1Label !== '브랜드') {
              selectedCategory.push(findCategory)
            }
          })
        })

        let gtmItems: any[] = []

        gtmItems = variations.map((variation: any, variationIndex: number) => {
          return {
            item_id: String(variation?.productNo),
            item_name: variation?.productName,
            affiliation: '어바웃피싱',
            item_list_price: variation?.price?.salePrice,
            item_selling_price: variation?.price?.salePrice - variation?.price?.immediateDiscountAmt,
            price: variation?.price?.salePrice - variation?.price?.immediateDiscountAmt,
            item_total_discount: variation?.price?.immediateDiscountAmt,
            quantity: variation.orderCnt,
            discount: 0,
            index: variationIndex,
            item_brand: variation?.brandName,
            item_category: selectedCategory[0]?.depth1Label,
            item_category2: selectedCategory[0]?.depth2Label,
            item_category3: selectedCategory[0]?.depth3Label,
            item_variant: variation?.optionTitle,
            promotion_id: undefined,
            promotion_name: undefined,
            item_list_id: undefined,
            item_list_name: undefined,
          }
        })

        const storageCartList = getParsedLocalStorageItem('cartList')

        if (storageCartList?.length !== 0) {
          gtmItems = gtmItems.map((item: any) => {
            const findItem = storageCartList?.find((storageItem: any) => storageItem.product === item.item_id)

            if (findItem) {
              return {
                ...item,
                promotion_id: findItem.promotion_id,
                promotion_name: findItem.promotion_name,
                item_list_id: findItem.item_list_id,
                item_list_name: findItem.item_list_name,
              }
            }

            return item
          })
        }

        return gtmItems
      } catch (error) {
        console.error(error)
      }
    },
    [categories]
  )

  const fetchFirstPurchase = useCallback(async () => {
    try {
      const { data: profileData } = await getProfile()
      const { data: orderData } = await getOrderList()

      if (!profileData || !orderData) return

      dispatch({
        type: SET_USER_PROFILE,
        payload: profileData,
      })

      const profileAdditionalInfo = JSON.parse(profileData.additionalInfo || '')

      if (profileAdditionalInfo.shopifyUserOrder || orderData.totalCount !== 0) {
        setFirstPurchase(true)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  const fetchUserCredit = useCallback(async () => {
    try {
      const { data } = await getCouponsMaximum(selectVariationCartNos.join(','))

      dispatch({
        type: SET_USER_CREDIT,
        payload: {
          totalDiscountAmt: data.totalDiscountAmt,
          cartCoupons: data.cartCoupons,
          productCoupons: data.productCoupons,
          fishingCoin: 0,
          accumulatedPoint: 0,
        },
      })
    } catch (error) {
      console.error(error)
    }
  }, [selectVariationCartNos])

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await getCategories()
      dispatch({
        type: SET_CATEGORIES,
        payload: data,
      })
    } catch (error) {
      console.error(error)
    }
  }, [])

  const fetchCartData = useCallback(async () => {
    setIsLottie(true)
    try {
      if (hasAccessToken) {
        const { data } = await getCart()
        return data
      } else {
        const guestCart = getParsedSessionStorageItem('guestCart')

        if (guestCart.length === 0) return

        const { data } = await postGuestCart(guestCart)

        window.sessionStorage.setItem('guestCart', JSON.stringify(guestCart))
        return data
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLottie(false)
    }
  }, [hasAccessToken])

  const generateOrderCartMap = (data: any) => {
    const { deliveryGroups, invalidProducts } = data

    const orderProductMap: any = deliveryGroups.reduce((acc: any, currentGroup: any) => {
      currentGroup.orderProducts.forEach((orderProduct: any) => {
        acc = {
          ...acc,
          [orderProduct.productNo]: {
            ...orderProduct,
          },
        }
      })
      return acc
    }, {})

    // NOTE: variation의 unique key = optionNo
    const orderVariationMap: any = Object.values(orderProductMap).reduce((acc: any, currentProduct: any) => {
      currentProduct.orderProductOptions.forEach((option: any) => {
        acc = {
          ...acc,
          [option.optionNo]: {
            imageUrl: currentProduct.imageUrl,
            productName: currentProduct.productName,
            brandName: currentProduct.brandName,
            categoryNos: currentProduct.categoryNos,
            couponUsable: currentProduct.couponUsable,
            accumulationUsable: currentProduct.accumulationUsable,
            buyAmt: currentProduct.buyAmt,
            ...option,
          },
        }
      })
      return acc
    }, {})

    /**
     * invalidProductsd의 옵션(variation)도 orderVariationMap에 추가
     * 장바구니에 유효한 상품인지 아닌지 상관 없이 노출
     * 구매불가 상품의 경우 재입고 신청하도록 유도
     */
    const invalidOrderVariationMap: any = invalidProducts.reduce((acc: any, currentProduct: any) => {
      currentProduct.orderProductOptions.forEach((option: any) => {
        acc = {
          ...acc,
          [option.optionNo]: {
            imageUrl: currentProduct.imageUrl,
            productName: currentProduct.productName,
            brandName: currentProduct.brandName,
            categoryNos: currentProduct.categoryNos,
            couponUsable: currentProduct.couponUsable,
            accumulationUsable: currentProduct.accumulationUsable,
            buyAmt: currentProduct.buyAmt,
            ...option,
          },
        }
      })

      return acc
    }, {})

    // 유효한 상품만 필터링해서 선택한 상품에 담기
    const selectOrderVariationMap: any = Object.values(orderVariationMap).reduce((acc: any, option: any) => {
      if (option.validInfo.valid) {
        acc = {
          ...acc,
          [option.optionNo]: {
            ...option,
          },
        }
      }
      return acc
    }, {})

    return {
      orderProductMap,
      orderVariationMap,
      invalidOrderVariationMap,
      selectOrderVariationMap,
    }
  }

  return {
    isInvalid,
    // user
    userProfile,
    firstPurchase,

    // price
    userCredit,
    totalCartPriceInfo,

    // product
    orderProductMap,
    // variation
    orderVariationMap,
    invalidOrderVariationMap,
    selectOrderVariationMap,
    selectVariationCartNos,

    // function
    dispatch,
    createGTMItems,
    fetchFirstPurchase,
    fetchUserCredit,
    fetchCategories,
    fetchCartData,
    generateOrderCartMap,
  }
}
